import { Activity, ArrowUp, ArrowDown, RefreshCw, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { AlertCircle } from "lucide-react";
import { useIperf3Test } from "@/hooks/useIperf3Test";

function getSpeedBadge(mbps: number) {
  if (mbps >= 100) return <Badge className="bg-green-500">Excellent</Badge>;
  if (mbps >= 50) return <Badge className="bg-green-600">Good</Badge>;
  if (mbps >= 10) return <Badge className="bg-yellow-500">Fair</Badge>;
  return <Badge variant="destructive">Poor</Badge>;
}

interface BandwidthTestCardProps {
  bridgeUrl: string;
}

export function BandwidthTestCard({ bridgeUrl }: BandwidthTestCardProps) {
  const { state, results, error, startTest, reset } = useIperf3Test();

  const isRunning = state === "starting-server" || state === "running-test";

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Activity className="h-5 w-5" />
          Bandwidth Test
        </CardTitle>
        <CardDescription>
          Test network throughput between this bridge and the API server using
          iperf3 (~20 seconds)
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {error && (
          <div className="flex items-center gap-3 rounded-md bg-destructive/10 p-3">
            <AlertCircle className="h-5 w-5 text-destructive shrink-0" />
            <div className="text-sm">
              <p className="font-medium">Test Failed</p>
              <p className="text-muted-foreground">{error}</p>
            </div>
          </div>
        )}

        {isRunning && (
          <div className="flex items-center gap-3 rounded-md bg-muted p-3">
            <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
            <div className="text-sm">
              {state === "starting-server"
                ? "Starting iperf3 server..."
                : "Running bandwidth test (~20s)..."}
            </div>
          </div>
        )}

        {results && (
          <div className="grid gap-4 sm:grid-cols-3">
            <div className="rounded-md border p-3">
              <div className="flex items-center gap-2 text-sm text-muted-foreground mb-1">
                <ArrowUp className="h-3.5 w-3.5 text-green-500" />
                Sender
              </div>
              <div className="text-xl font-bold">
                {results.sentMbps.toFixed(1)} Mbps
              </div>
              <div className="mt-1">{getSpeedBadge(results.sentMbps)}</div>
            </div>

            <div className="rounded-md border p-3">
              <div className="flex items-center gap-2 text-sm text-muted-foreground mb-1">
                <ArrowDown className="h-3.5 w-3.5 text-blue-500" />
                Receiver
              </div>
              <div className="text-xl font-bold">
                {results.receivedMbps.toFixed(1)} Mbps
              </div>
              <div className="mt-1">{getSpeedBadge(results.receivedMbps)}</div>
            </div>

            <div className="rounded-md border p-3">
              <div className="text-sm text-muted-foreground mb-1">Details</div>
              <div className="space-y-1 text-sm">
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Retransmits</span>
                  <span className="font-medium">{results.retransmits}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Duration</span>
                  <span className="font-medium">
                    {results.durationSeconds.toFixed(1)}s
                  </span>
                </div>
              </div>
            </div>
          </div>
        )}

        <div className="flex gap-2">
          {state === "idle" || state === "error" ? (
            <Button onClick={() => startTest(bridgeUrl)}>
              <Activity className="h-4 w-4 mr-2" />
              Test Bandwidth
            </Button>
          ) : state === "completed" ? (
            <Button variant="outline" onClick={() => startTest(bridgeUrl)}>
              <RefreshCw className="h-4 w-4 mr-2" />
              Run Again
            </Button>
          ) : null}
          {state === "completed" && (
            <Button variant="ghost" onClick={reset}>
              Clear
            </Button>
          )}
        </div>
      </CardContent>
    </Card>
  );
}
